import { Client, getStateCallbacks, type Room } from "colyseus.js";
import {
  ROOM_NAME,
  buildingConfigFromOverride,
  vigorZoneFromOverride,
  type BuildingConfig,
  type VigorZoneData,
} from "@fortress/shared";
import {
  clearSession,
  ensureValidAccessToken,
  refreshStoredSession,
} from "../auth/session";
import { getAppState } from "../appState";
import { serverWsUrl } from "../clientConfig";
import { recordServerClockSample, resetServerClock } from "./serverClock";
import type {
  BuildingConfigState,
  CardPile,
  ChatMessage,
  CursorState,
  FortressCardState,
  GameState,
  HistoryEntry,
  HistoryGroup,
  NodeState,
  Player,
  QueuedMove,
  RestorePoint,
  Spectator,
  TilePlacement,
  VigorZoneState,
} from "@fortress/shared/schema";

export const client = new Client(serverWsUrl());

/**
 * Client → server message names. Mirrors the server's `ClientCommand`
 * (server/src/commands/protocol.ts); kept in sync by hand until the wire
 * protocol is hoisted into `@fortress/shared`.
 */
export const ClientMessage = {
  Ready: "ready",
  Action: "action",
  EndTurn: "end_turn",
  Pass: "pass",
  /** Take back our most recent Construct Building / Construct Bridge action,
   * while the turn is still ours. No payload — the server holds the snapshot
   * (`state.undoLabel` names what it would take back, "" when nothing). */
  Undo: "undo",
  /** Match creator only: roll the whole match back to one of the boundaries in
   * `state.restorePoints`. Payload `{ restoreId }`. Unlike `Undo` this is not
   * scoped to our own turn or our own move — the board, everyone's cards and
   * resources, and the move history all go back to that moment. */
  Restore: "restore",
  /** Defense window: this player is done aiming reactive defenders. */
  DefenseDone: "defense_done",
  MartyrDiscard: "martyr_discard",
  /** Answer the live Zapped Bapped pay window: `{ pay }` — pay matches the
   * offered energy onto the card's stored pile; otherwise the window just
   * closes. It also closes on its own when the next action finalizes. */
  VigorOffer: "vigor_offer",
  /** Set our Zapped Bapped auto-pay for queued-move volleys: `{ enabled,
   * floor }` — while enabled the server matches auto-played opponents'
   * penalties as long as at least `floor` Energy stays banked afterwards. */
  VigorAutoPay: "vigor_auto_pay",
  /** Set our standing reserve for Swole Turtle's round-end energy deposit:
   * `{ floor }` — at every round end, banked Energy above `floor` moves onto
   * the card as tokens (0 = deposit everything). */
  VigorDepositFloor: "vigor_deposit_floor",
  /** Set our BAM Exploder round-end auto-destroy: `{ enabled }` — while on
   * (the default) every opponent fortress within our attack range of our
   * Artifacts of Courage is destroyed at round end. */
  ArtifactAutoDestroy: "artifact_auto_destroy",
  /** Set our Telescopic Arm standing capture answer:
   * `{ enabled, resource }` — while on, our successful Barracks attacks
   * capture the destroyed building instead of razing it, paying the victim
   * one resource of kind `resource` (an index into `[minerals, food, vigor,
   * obsidian]`). */
  CaptureSettings: "capture_settings",
  /** Answer one pending post-combat Barracks upkeep choice:
   * `{ nodeId, keep }` — keep pays the Food, otherwise the Barracks starves. */
  BarracksUpkeep: "barracks_upkeep",
  /** Answer one pending post-combat resource steal: `{ victimId, resources }`
   * — resources is a `[minerals, food, vigor, obsidian]` tuple of what to take
   * from the victim, summing to at most the entry's count. */
  StealResources: "steal_resources",
  /** Lobby, host only: `{ ranked, poolId, poolName }` — whether the match is
   * ranked and which skill rating pool it reports to. */
  RankedSettings: "ranked_settings",
  /** Lobby, host only: `{ minutes }` — the max minutes per move, 0 for no
   * limit. The server re-sends `ServerMessage.Config` with the new limit. */
  TurnTimeLimit: "turn_time_limit",
  /** Lobby, creator only: close the lobby for everyone. The server broadcasts
   * `ServerMessage.LobbyClosed` and disposes the room. No payload. */
  KillLobby: "kill_lobby",
  /** Setup phase: choose this player's home-base node. */
  PlaceHome: "place_home",
  /** Production phase: assign this player's starting production `[m, f, v, o]`. */
  SetProduction: "set_production",
  /** Dev-only (non-prod), shown only when `IAppState.devMode`: force-start the
   * match, auto-assigning every player's home base + production. */
  AutoInit: "auto_init",
  /** Queue a `GameAction` for an upcoming turn: `{ slot, action, label }`. */
  QueueMove: "queue_move",
  /** Remove one queued move: `{ slot, index }` — index within that slot. */
  UnqueueMove: "unqueue_move",
  /** Answer the open round-start cycle window (first player only):
   * `{ regular: cardId|null, win: cardId|null }` — both null keeps both
   * markets ("cycle nothing"). */
  RoundStartCycle: "round_start_cycle",
  /** Queue (or `{ clear: true }` to remove) the round-start cycle choice for
   * the next round. Marker holder only; executed only if they open the round. */
  QueueRoundStartCycle: "queue_round_start_cycle",
  /** Live pointer update `{ area, x, y, target }`, mirrored to everyone via
   * `state.cursors`. Board coords are hex units from the board centre (see
   * `PixiGame.boardPointFromClient`); `{ area: "" }` hides our cursor. */
  Cursor: "cursor",
  /** One-shot touch tap (same shape as `Cursor`, minus the hide form) — the
   * server relays it to every other client as `ServerMessage.CursorTap`.
   * Sent by touch pointers, which have no hover cursor to stream. */
  CursorTap: "cursor_tap",
  /** Emoji reaction pinned to a point: a cursor-shaped payload plus `emoji`.
   * Relayed to every other client as `ServerMessage.CursorEmoji`; the sender
   * echoes it locally. */
  CursorEmoji: "cursor_emoji",
  /** Send a chat message: `{ text }` (emoji allowed). The server snapshots the
   * sender's name/seat and appends it to the synced `chatMessages` log. */
  Chat: "chat",
  /** Toggle one card on our private fortress watchlist: `{ cardId }`. The
   * server flips it in our view-gated `watchedFortresses` and, for account
   * holders, persists the list across matches. */
  ToggleWatch: "toggle_watch",
  /** Tutorial match only: skip the current step (the server plays its move
   * for us, or just moves on for a read-only step). No payload. */
  TutorialSkip: "tutorial_skip",
  /** Clock-sync probe `{ sentAt }` (our `Date.now()`); answered with
   * `ServerMessage.Clock` (see `serverClock.ts`). */
  ClockSync: "clock_sync",
} as const;

/** Server → client event names (mirrors server's `ServerEvent`). */
export const ServerMessage = {
  /** A rejected command (e.g. illegal home placement, not your turn). */
  Error: "error",
  /** Turn advanced. The client drives UI off synced state, so this is unused —
   * we register a no-op only to silence colyseus.js's "not registered" warning. */
  TurnChanged: "turn_changed",
  /** Sent only to the player whose turn it just became — fires the one-shot
   * "It's Your Turn" toast + OS notification. The persistent tab title is driven
   * off synced `currentPlayerId`, not this event. */
  YourTurn: "your_turn",
  /** The match's pinned `GameConfig` snapshot, sent once on join. */
  Config: "config",
  /** The lobby creator closed the lobby — arrives just before the room
   * disconnects, so the client can tell a kill from a network drop.
   * Payload `{ byName }`. */
  LobbyClosed: "lobby_closed",
  /** Our account just joined from ANOTHER device — that connection now holds
   * our seat, and this one is closed (CONSENTED) right after. No payload. */
  SessionSuperseded: "session_superseded",
  /** Another player performed an action — payload is a shared `ActionNotice`,
   * surfaced as a transient popup anchored to that player's sidebar row. */
  ActionPerformed: "action_performed",
  /** Targeted at this player when a defense window opens on them: buildings of
   * theirs — or bridges anyone may defend — would fall when the ending turn
   * resolves. Payload `{ nodeIds, bridgeKeys }` — the at-risk nodes and bridge
   * edge keys. Fire-once cue; persistent UI reads the synced state. */
  UnderAttack: "under_attack",
  /** A win condition resolved and the match is over. Payload
   * `{ winnerId: string | null, winCardId: string }` (null = draw). Fire-once;
   * the result banner reads the synced `winnerId` / `winCardId` state instead
   * (which also covers reconnects and late-joining spectators). */
  GameOver: "game_over",
  /** Queued-move auto-play stopped on our turn (a move was no longer valid or
   * needs live input). Payload `{ label, reason, dropped }`. */
  QueueHalted: "queue_halted",
  /** The creator rolled the match back — sent to EVERYONE including them, so
   * every client can explain the board that just changed under it. Payload
   * `{ byName, round, turn, dropped }`. Fire-once cue; the permanent record is
   * the "Restored to …" entry in the synced move history. */
  Restored: "restored",
  /** Another client (touch) tapped a shared surface — `{ id, area, x, y,
   * target }`, rendered as a transient tap ripple where it landed. */
  CursorTap: "cursor_tap",
  /** Another client dropped an emoji reaction — `{ id, area, x, y, target,
   * tx, ty, emoji }`, played once at that spot (pulsate in, fade out). */
  CursorEmoji: "cursor_emoji",
  /** Cosmetic Barracks animation cues — `{ fx: BoardFx[] }`: a tap (aim), and
   * turn-end destroyed / defended buildings and starved Barracks. Fire-once;
   * the synced state is the truth. */
  BoardFx: "board_fx",
  /** Reply to a `ClientMessage.ClockSync` probe: `{ sentAt, serverNow }` —
   * our send time echoed, and the server's clock as it answered. */
  Clock: "clock",
} as const;

/**
 * Run a Colyseus join with a server-validated access token (unless
 * AUTH_DISABLED). Only the JWT travels in the join options. If it expires in
 * the window between validation and the WebSocket upgrade, the server rejects
 * the join with "expired" — refresh once (the only time the refresh token is
 * sent, to `POST /auth/refresh`) and retry.
 */
async function authedJoin(
  join: (accessToken?: string) => Promise<Room<GameState>>,
): Promise<Room<GameState>> {
  const { authDisabled } = await getAppState();
  if (authDisabled) return join(undefined);
  const tokens = await ensureValidAccessToken();
  if (!tokens) throw new Error("not_authenticated");
  try {
    return await join(tokens.accessToken);
  } catch (err) {
    if (!(err instanceof Error) || !err.message.includes("expired")) throw err;
    // Throws on transient refresh failures — propagates, tokens kept.
    const fresh = await refreshStoredSession();
    if (!fresh) {
      // Definitive rejection — the session is dead.
      await clearSession();
      throw new Error("not_authenticated");
    }
    return join(fresh.accessToken);
  }
}

/** When a fresh room connection probes the server clock (ms after join). The
 * first probe can queue behind the join's state decode; later ones usually
 * see a tighter round trip, and the tightest wins (`serverClock.ts`). */
const CLOCK_PROBE_DELAYS_MS = [0, 1500, 5000];

/**
 * Baseline handlers every joined room gets: surface rejected commands in the
 * console (so a silently-dropped action is at least visible instead of looking
 * like a hang), a no-op for `turn_changed` (state sync drives the UI;
 * registered only to silence colyseus.js's "not registered" warning), and the
 * server-clock probes that `serverNow()` reads.
 */
function attachBaseHandlers(room: Room<GameState>): Room<GameState> {
  room.onMessage(
    ServerMessage.Error,
    (e: { code?: string; message?: string }) => {
      console.error(`[server] ${e?.code ?? "error"}: ${e?.message ?? ""}`);
    },
  );
  room.onMessage(ServerMessage.TurnChanged, () => {});
  room.onMessage(
    ServerMessage.Clock,
    (m: { sentAt?: unknown; serverNow?: unknown }) => {
      if (typeof m?.sentAt === "number" && typeof m?.serverNow === "number") {
        recordServerClockSample(m.sentAt, m.serverNow);
      }
    },
  );
  resetServerClock();
  const probes = CLOCK_PROBE_DELAYS_MS.map((delay) =>
    setTimeout(
      () => room.send(ClientMessage.ClockSync, { sentAt: Date.now() }),
      delay,
    ),
  );
  room.onLeave(() => probes.forEach((id) => clearTimeout(id)));
  return room;
}

/**
 * Join a specific lobby/match by Colyseus room id. Ensures a fresh access token
 * first (unless AUTH_DISABLED), then `joinById`.
 */
export async function joinGameById(
  roomId: string,
  opts?: { spectate?: boolean },
): Promise<Room<GameState>> {
  const spectate = opts?.spectate === true;
  const room = await authedJoin((accessToken) =>
    client.joinById<GameState>(roomId, {
      name: spectate ? "Spectator" : "Player",
      accessToken,
      ...(spectate ? { spectate: true } : {}),
    }),
  );
  return attachBaseHandlers(room);
}

/**
 * Re-enter a live match this client disconnected from (page refresh / app
 * reload), using the colyseus.js reconnection token stored while the match was
 * running. Only honoured while the server is still holding the seat
 * (`GAME_RECONNECT_GRACE_S`); afterwards it rejects with "reconnection token
 * invalid or expired". The reconnect path skips the room's `onAuth` — the
 * token itself is the credential — so no access token travels.
 */
export async function reconnectGame(
  reconnectionToken: string,
): Promise<Room<GameState>> {
  const room = await client.reconnect<GameState>(reconnectionToken);
  return attachBaseHandlers(room);
}

/**
 * @deprecated Prefer create/browse/code → `joinGameById`. Kept for AUTH_DISABLED
 * quick paths that still want joinOrCreate.
 */
export async function joinGame(name: string): Promise<Room<GameState>> {
  // With auth disabled (the server's AUTH_DISABLED, advertised via app state),
  // join without a token — onAuth seats a guest and ignores it.
  const room = await authedJoin((accessToken) =>
    client.joinOrCreate<GameState>(ROOM_NAME, { name, accessToken }),
  );
  return attachBaseHandlers(room);
}

/** Lobby metadata synced on GameState while phase === "lobby". */
export interface LobbyInfo {
  hostUserId: string;
  /** Account that created the match — unlike `hostUserId` this outlives the
   * lobby (it is re-seeded on resume), and it is who may roll the match back.
   * "" for guest-created matches. */
  creatorUserId: string;
  inviteCode: string;
  visibility: string;
  maxPlayers: number;
  autoStartAtMs: number;
  /** Match reports its result to `poolId` when it ends. */
  ranked: boolean;
  poolId: string;
  poolName: string;
}

/** The four resource counts, in canonical order. */
export interface ResourceCounts {
  minerals: number;
  food: number;
  vigor: number;
  obsidian: number;
}

/** One privately queued move (only ever present for the owning client — the
 * field is `@view()`-gated on the wire, so other players' lists sync empty). */
export interface QueuedMoveView {
  /** 1 = the owner's next turn, up to MAX_QUEUE_TURNS ahead. */
  slot: number;
  /** Human-readable summary shown in the queue panel. */
  label: string;
  /** The queued `GameAction`, JSON-encoded. */
  payload: string;
}

/** One of a player's implemented (face-up) Fortress cards. */
export interface FortressCardView {
  cardId: string;
  tokens: number;
  /** Resources stored ON the card (e.g. Bursted Flap's energy pile). */
  stored: number;
  disabled: boolean;
  /** Big Boom's rescale of this instance's token cap: 1 doubled, -1 halved,
   * 0 the printed cap (read through `instanceTokenCap`). */
  capScale: number;
  /** True on a phantom instance a CurdleCat awarded ("copy the effect") —
   * a full card for every rule, never on the board. */
  copied: boolean;
  /** On the COPIER (CurdleCat): the card id of its awarded copy, "" when
   * none. */
  copiedCardId: string;
  /** Card ids this fortress marked (Dog Den) — marks stay once made. */
  markedCardIds: string[];
  /** A marked win-deck card has been implemented (Dog Den). */
  markedWinImplemented: boolean;
  /** A marked regular-deck card has been implemented (Dog Den). */
  markedRegularImplemented: boolean;
  zoneLevel: number;
  /** The piece the owner placed on the card to denote their implement-time
   * pick (Shifty Engineer), "" when none. */
  choice: string;
  /** Board node ids this fortress occupies, for highlight / focus on the board. */
  nodeIds: number[];
  bridgeKeys: string[];
}

/** A watcher, flattened from the synced `Spectator` schema. */
export interface SpectatorView {
  id: string;
  name: string;
}

/** One client's live pointer, flattened from the synced `CursorState` schema
 * (keyed by session id — players and spectators alike; hidden cursors simply
 * aren't in the list). Board coords are camera-independent hex units from the
 * board centre; sidebar/lobby coords are normalised 0..1 with `target` naming
 * the exact element hovered (`card:<id>` / `player:<id>` / `history:<id>` /
 * `lobby:ready`). */
export interface CursorView {
  id: string;
  area: string;
  x: number;
  y: number;
  target: string;
  /** Position within the `target` element, normalised 0..1 (0.5 = centre) —
   * lets receivers place the cursor on the exact spot of their own copy of
   * that card/row. Meaningless when `target` is "". */
  tx: number;
  ty: number;
}

/** A roster entry, flattened from the synced `Player` schema. Used both by the
 * lobby (name/ready) and the in-game player sidebar (resources/fortresses). */
export interface LobbyPlayer {
  id: string;
  name: string;
  seat: number;
  ready: boolean;
  connected: boolean;
  passed: boolean;
  /**
   * This player's standing in the lobby's skill rating pool. Only populated
   * while the match is ranked with a pool picked; `ratingLoaded` is false
   * otherwise (and until the server's lookup lands).
   *
   * An unrated player carries the pool's starting rating with a zeroed record
   * — the same shape as someone rated but yet to finish a match, because both
   * mean "no matches".
   */
  ratingLoaded: boolean;
  rating: number;
  ratingWins: number;
  ratingLosses: number;
  ratingMatches: number;
  /** Positive = consecutive wins, negative = losses, 0 = neither. */
  ratingStreak: number;
  /** Wall-clock epoch ms their current turn started, or 0 when it's not their
   * turn / clock paused. Used for live countdown (timed) and count-up total. */
  turnStartedMs: number;
  /** Cumulative ms spent on completed turns this match. Live total is
   * `turnTimeMs + (now - turnStartedMs)` while the clock is armed. */
  turnTimeMs: number;
  /** Setup: true once this player has placed their home base. */
  homePlaced: boolean;
  /** Graph node id of this player's home base, or -1 before it's placed. */
  homeNodeId: number;
  /** Setup: true once this player has confirmed their starting production. */
  productionSet: boolean;
  /** Resources on hand. */
  resources: ResourceCounts;
  /** Production Track allocation, collected each Production phase. */
  production: ResourceCounts;
  /** Implemented (face-up) Fortress cards. */
  fortressCards: FortressCardView[];
  /** Face-down cards in hand. Only synced to the owning client (view-gated), so
   * this is empty for everyone else. */
  hiddenCards: string[];
  pendingReorderCards: string[];
  pendingReorderMarket: string;
  pendingMartyrDiscards: string[];
  /** Timed matches: when the front Martyr choice was put to us (0 = none). */
  martyrPromptStartedMs: number;
  /** The live Zapped Bapped pay window (energy amount, 0 = closed) — open
   * until the next action finalizes. Own client only (view-gated). */
  pendingVigorOffer: number;
  /** Our queued-volley auto-pay setting (own client only, view-gated). */
  vigorAutoPay: boolean;
  vigorAutoPayFloor: number;
  /** Our standing reserve for Swole Turtle's round-end energy deposit (own
   * client only, view-gated): banked Energy above it moves onto the card at
   * every round end (0 = deposit everything). */
  vigorDepositFloor: number;
  /** Our BAM Exploder round-end auto-destroy setting (own client only,
   * view-gated; true = destroy everything in range of our Artifacts of
   * Courage at round end). */
  artifactAutoDestroy: boolean;
  /** Our Telescopic Arm standing capture settings (own client only,
   * view-gated): while `captureAuto` is on, successful Barracks attacks
   * capture instead of destroying, paying the victim one resource of kind
   * `captureGiveResource` (an index into `[minerals, food, vigor, obsidian]`). */
  captureAuto: boolean;
  captureGiveResource: number;
  /** Privately queued moves for upcoming turns (own client only; empty for
   * everyone else — the field is view-gated like the hidden hand). */
  queuedMoves: QueuedMoveView[];
  /** Queued round-start cycle choice as JSON (`{"regular":id|null,"win":id|null}`),
   * or "" when none. Own client only (view-gated). */
  queuedRoundStartCycle: string;
  /** Watched fortress card ids (own client only — view-gated like the hand;
   * empty for everyone else). */
  watchedFortresses: string[];
  baseFortresses: number;
  apexFortresses: number;
  /** Banked iCarlie "gain 2 of any resource" awards, cashed via the tray. */
  pendingResourceClaims: number;
  /** Banked single "+1 of any resource" picks (Resource Sequencer income;
   * Frienemy on games pinned to "31" and earlier). */
  pendingResourcePicks: number;
  /** Banked Pee-Pee Pinata one-time units still to distribute. */
  pendingResourceDrops: number;
  /** Banked Miss Mineralverse Maggot market reactions (a free cycle of one
   * card, or a lock/unlock, per own build on a one-time resource). */
  pendingMarketActions: number;
  /**
   * Sparse building-config overrides (keyed by building type). Missing keys
   * fall back to the match `GameConfig` via `resolveBuilding`.
   */
  buildings: Record<string, BuildingConfig>;
  /**
   * Sparse vigor-zone overrides (keyed by altitude level "0".."4"). Missing
   * keys fall back to the match `GameConfig` via `resolveVigorZone`.
   */
  vigorZones: Record<string, VigorZoneData>;
}

/** One Fortress deck, flattened for the market sidebar: its face-up row plus the
 * sizes of the (hidden) draw pile and the discard pile. */
export interface MarketPile {
  /** Face-up row of card ids players may implement / cycle. */
  cards: string[];
  /** Face-up card ids that are locked: can't be cycled, still purchasable. */
  locked: string[];
  /** Cards remaining face-down in the draw pile. */
  deckCount: number;
  /** Cards in the discard pile. */
  discardCount: number;
  /** Discard pile card ids, oldest first (the last entry is the most recent
   * discard). Public knowledge — cards are always discarded face-up — so the
   * market sidebar can offer the pile for browsing. */
  discarded: string[];
  /** The draw pile's card ids, SORTED so the draw order never leaks — the
   * searchable pool for Poopy Investigator's tutor action. */
  pool: string[];
}

/** The market state of both Fortress decks. */
export interface MarketView {
  /** Regular Fortress deck. */
  regular: MarketPile;
  /** Win (victory) Fortress deck. */
  win: MarketPile;
}

/** One assembled-board tile, flattened from the synced `TilePlacement` schema. */
export interface PlacementView {
  slot: string;
  tileId: string;
  face: string;
  rotation: number;
}

/** One labelled detail group of a history entry (flattened from `HistoryGroup`). */
export interface HistoryGroupView {
  label: string;
  items: string[];
}

/** One recorded action, flattened from the synced `HistoryEntry` for the Turn
 * History view. Oldest-first as stored; the UI reverses it for newest-on-top. */
export interface HistoryEntryView {
  id: number;
  playerId: string;
  playerName: string;
  seat: number;
  title: string;
  round: number;
  turn: number;
  /** Board node the action centred on, or `-1` for none. */
  focusNodeId: number;
  groups: HistoryGroupView[];
}

/**
 * One point the match creator may roll the match back to, flattened from the
 * synced `RestorePoint`. Oldest first, as stored.
 *
 * `afterHistoryId` is how the Turn History places it: every entry with a
 * higher id happened after this moment, so the point is drawn immediately
 * before the first such row.
 */
export interface RestorePointView {
  id: number;
  round: number;
  turn: number;
  /** "turn" — the start of that turn; "round" — the start of that round,
   * before its free market cycle and income. */
  kind: string;
  /** Player whose turn begins here ("" for a round boundary). */
  playerName: string;
  seat: number;
  afterHistoryId: number;
}

/** One chat message, flattened from the synced `ChatMessage` for the chat panel.
 * Oldest-first as stored; the panel renders top-to-bottom (newest at the bottom).
 * `seat` is `SPECTATOR_SEAT` (255) for a message sent by a spectator. */
export interface ChatMessageView {
  id: number;
  senderId: string;
  senderName: string;
  seat: number;
  text: string;
  /** Epoch ms when the server accepted the message. */
  at: number;
}

export interface TutorialView {
  active: boolean;
  step: number;
  nodeIds: number[];
  /** Canonical `"lo-hi"` edge key of the step's bridge spot, or "". */
  edge: string;
}

export interface StateWatchers {
  /** Called whenever a node is added or changes (owner / resource / severed). */
  onNode: (node: NodeState, seatOf: ReadonlyMap<string, number>) => void;
  /** Called whenever the active turn number changes. */
  onTurn: (turnNumber: number) => void;
  /** Called whenever the round number changes. */
  onRound?: (roundNumber: number) => void;
  /** Called whenever the action points left in the current turn change. */
  onActionsRemaining?: (actionsRemaining: number) => void;
  /** Called whenever the current player's undoable action changes: the label
   * of the construction they may still take back ("Build Scout"), or "" when
   * there is nothing to undo. */
  onUndoLabel?: (label: string) => void;
  /** Called whenever the game's cumulative implemented-fortress count changes
   * — Inverse Molehill's growing win-deck discount reads it. */
  onFortressesImplemented?: (count: number) => void;
  /** Tutorial match: the current step index and what it points at on the
   * board. Fires whenever any of them changes (and once on attach). On an
   * ordinary match it fires once with `active: false`. */
  onTutorial?: (tutorial: TutorialView) => void;
  /** Called whenever the match's seated-player count (pinned at go-live) is
   * known — a `costSplitByPlayers` card (Horny Neighbour) divides by it. */
  onStartingPlayerCount?: (count: number) => void;
  /** Called whenever the Space Cheese Curdler last-stand state changes:
   * `playerId` is the holder playing their solo round ("" when none),
   * `turnsRemaining` how many of its turns are left (the one in progress
   * included), and the pending fields name the postponed winner and their
   * win card. */
  onLastStand?: (info: {
    playerId: string;
    turnsRemaining: number;
    pendingWinnerId: string;
    pendingWinCardId: string;
  }) => void;
  /** Called whenever the active player (whose turn it is) changes. */
  onCurrentPlayer?: (currentPlayerId: string) => void;
  /** Called whenever the First-Turn marker holder changes. */
  onFirstPlayer?: (firstPlayerId: string) => void;
  /** Called whenever the round-start cycle window opens/closes — the session
   * id of the first player it waits on, or "" when closed. */
  onRoundStartCycle?: (playerId: string) => void;
  /** Called whenever the match phase changes (`lobby` → `playing` → `ended`). */
  onPhase?: (phase: string) => void;
  /** Called whenever lobby metadata fields change (code, host, countdown…). */
  onLobbyInfo?: (info: LobbyInfo) => void;
  /** Called whenever the lobby roster changes (join/leave/ready/rename). */
  onPlayers?: (players: LobbyPlayer[]) => void;
  /** Called whenever either Fortress market's face-up row changes. */
  onMarkets?: (markets: MarketView) => void;
  /** Called when the board is (re)assembled — the full tile placement list. */
  onPlacements?: (placements: PlacementView[]) => void;
  /** Called when severed paths (chasms) change — canonical `"lo-hi"` edge keys. */
  onSevered?: (severedEdges: string[]) => void;
  /** Called when constructed Bridges change — canonical `"lo-hi"` edge keys. */
  onBridges?: (bridges: string[]) => void;
  /** Called whenever the move history grows — the full list, oldest first. */
  onHistory?: (history: HistoryEntryView[]) => void;
  /** Called whenever the creator's restore ring changes — the full list,
   * oldest first. Points are appended at turn/round boundaries, dropped off
   * the front as they age out, and dropped off the BACK by a rollback. */
  onRestorePoints?: (points: RestorePointView[]) => void;
  /** Called whenever the chat log grows (or is trimmed) — the full list, oldest
   * first (capped at the last 1000 by the server). */
  onChat?: (chat: ChatMessageView[]) => void;
  /** Called whenever the defense window opens/closes or its pending-defender
   * list changes. `pending` is the session ids still to confirm. `midTurn` is
   * true for a mid-turn forced-attack designation window (Stab Pole Hole) —
   * closing it resumes the designator's turn instead of resolving combat. */
  onDefense?: (defense: {
    active: boolean;
    midTurn: boolean;
    pending: string[];
  }) => void;
  /** Called whenever the post-combat Barracks upkeep window changes — the
   * unresolved entries, each encoded `"playerId|nodeId|foodCost"`. Non-empty
   * = the ending turn is frozen until every owner keeps or starves theirs. */
  onUpkeep?: (entries: string[]) => void;
  /** Called whenever the post-combat resource-steal window changes — the
   * unresolved entries, each encoded `"attackerId|victimId|count"`. Non-empty
   * = the ending turn is frozen until every attacker picks their plunder. */
  onSteals?: (entries: string[]) => void;
  /** Called whenever the spectator roster changes (someone starts/stops
   * watching). */
  onSpectators?: (spectators: SpectatorView[]) => void;
  /** Called whenever any live cursor moves, appears or hides — the full list
   * of visible cursors (self included; filter by session id). Fires at the
   * room's patch rate while pointers move, so keep the handler cheap. */
  onCursors?: (cursors: CursorView[]) => void;
  /** Called once the match has ended with the synced result — the winner's
   * session id ("" = draw / unknown) and the fortress card whose win condition
   * fired. Fires on live game-over AND on (re)joining an already-ended room. */
  onGameResult?: (result: { winnerId: string; winCardId: string }) => void;
}

/**
 * Bridges Colyseus schema callbacks to plain handlers. All version-specific
 * colyseus.js callback wiring is contained in this one function.
 */
export function watchState(
  room: Room<GameState>,
  watchers: StateWatchers,
): void {
  const $ = getStateCallbacks(room);
  const seatOf = new Map<string, number>();

  const emitPlayers = () => {
    if (!watchers.onPlayers) return;
    const roster: LobbyPlayer[] = [];
    room.state.players.forEach((p: Player, id: string) => {
      const buildings: Record<string, BuildingConfig> = {};
      p.buildings?.forEach((b: BuildingConfigState, key: string) => {
        buildings[key] = buildingConfigFromOverride(b);
      });
      const vigorZones: Record<string, VigorZoneData> = {};
      p.vigorZones?.forEach((z: VigorZoneState, key: string) => {
        vigorZones[key] = vigorZoneFromOverride(z);
      });
      roster.push({
        id,
        name: p.name,
        seat: p.seat,
        ready: p.ready,
        connected: p.connected,
        passed: p.passed,
        ratingLoaded: p.ratingLoaded ?? false,
        rating: p.rating ?? 0,
        ratingWins: p.ratingWins ?? 0,
        ratingLosses: p.ratingLosses ?? 0,
        ratingMatches: p.ratingMatches ?? 0,
        ratingStreak: p.ratingStreak ?? 0,
        turnStartedMs: p.turnStartedMs,
        turnTimeMs: p.turnTimeMs ?? 0,
        homePlaced: p.homeNodeId >= 0,
        homeNodeId: p.homeNodeId,
        productionSet: p.productionSet,
        resources: {
          minerals: p.resources?.minerals ?? 0,
          food: p.resources?.food ?? 0,
          vigor: p.resources?.vigor ?? 0,
          obsidian: p.resources?.obsidian ?? 0,
        },
        production: {
          minerals: p.production?.minerals ?? 0,
          food: p.production?.food ?? 0,
          vigor: p.production?.vigor ?? 0,
          obsidian: p.production?.obsidian ?? 0,
        },
        fortressCards: p.fortressCards
          ? [...p.fortressCards].map((c: FortressCardState) => ({
              cardId: c.cardId,
              tokens: c.tokens,
              stored: c.stored ?? 0,
              disabled: c.disabled,
              capScale: c.capScale ?? 0,
              copied: c.copied ?? false,
              copiedCardId: c.copiedCardId ?? '',
              markedCardIds: c.markedCardIds ? [...c.markedCardIds] : [],
              markedWinImplemented: c.markedWinImplemented ?? false,
              markedRegularImplemented: c.markedRegularImplemented ?? false,
              zoneLevel: c.zoneLevel ?? -1,
              choice: c.choice ?? '',
              nodeIds: c.nodeIds ? [...c.nodeIds] : [],
              bridgeKeys: c.bridgeKeys ? [...c.bridgeKeys] : [],
            }))
          : [],
        hiddenCards: p.hiddenCards ? [...p.hiddenCards] : [],
        pendingReorderCards: p.pendingReorderCards
          ? [...p.pendingReorderCards]
          : [],
        pendingReorderMarket: p.pendingReorderMarket ?? '',
        pendingMartyrDiscards: p.pendingMartyrDiscards
          ? [...p.pendingMartyrDiscards]
          : [],
        martyrPromptStartedMs: p.martyrPromptStartedMs ?? 0,
        pendingVigorOffer: p.pendingVigorOffer ?? 0,
        vigorAutoPay: p.vigorAutoPay ?? false,
        vigorAutoPayFloor: p.vigorAutoPayFloor ?? 0,
        vigorDepositFloor: p.vigorDepositFloor ?? 0,
        artifactAutoDestroy: p.artifactAutoDestroy ?? true,
        captureAuto: p.captureAuto ?? false,
        captureGiveResource: p.captureGiveResource ?? 0,
        queuedMoves: p.queuedMoves
          ? [...p.queuedMoves].map((m) => ({
              slot: m.slot,
              label: m.label,
              payload: m.payload,
            }))
          : [],
        queuedRoundStartCycle: p.queuedRoundStartCycle ?? '',
        watchedFortresses: p.watchedFortresses ? [...p.watchedFortresses] : [],
        baseFortresses: p.baseFortresses,
        apexFortresses: p.apexFortresses,
        pendingResourceClaims: p.pendingResourceClaims ?? 0,
        pendingResourcePicks: p.pendingResourcePicks ?? 0,
        pendingResourceDrops: p.pendingResourceDrops ?? 0,
        pendingMarketActions: p.pendingMarketActions ?? 0,
        buildings,
        vigorZones,
      });
    });
    roster.sort((a, b) => a.seat - b.seat);
    watchers.onPlayers(roster);
  };

  // Seats are (re)assigned at game start — after a player's `onAdd` already
  // captured its join-order seat — so `seatOf` would otherwise go stale and every
  // board token would render with the wrong (often shared) seat colour. Recolour
  // all nodes (coalesced) whenever a seat changes.
  let recolorScheduled = false;
  const scheduleRecolor = () => {
    if (recolorScheduled) return;
    recolorScheduled = true;
    queueMicrotask(() => {
      recolorScheduled = false;
      room.state.nodes.forEach((node: NodeState) =>
        watchers.onNode(node, seatOf),
      );
    });
  };

  // Players are keyed by seat key — the owning account's id, the same string
  // every `ownerId` / `currentPlayerId` names.
  $(room.state).players.onAdd((player: Player, playerId: string) => {
    seatOf.set(playerId, player.seat);
    // Nodes may decode before their owners: a captured piece's origin seat is
    // unknown until now, so repaint once the seat lands.
    scheduleRecolor();
    $(player).listen("seat", (seat: number) => {
      if (seatOf.get(playerId) === seat) return;
      seatOf.set(playerId, seat);
      scheduleRecolor();
    });
    $(player).onChange(emitPlayers);
    // Resources / production live on nested schemas, and the card collections are
    // nested too — none of these bubble up to the parent's onChange, so subscribe
    // to each directly (via the proxy, which tolerates a not-yet-decoded field).
    $(player).resources.onChange(emitPlayers);
    $(player).production.onChange(emitPlayers);
    $(player).fortressCards.onAdd((card: FortressCardState) => {
      $(card).onChange(emitPlayers);
      // The node list mutates in place (move-fortress) — array content
      // changes don't fire the card's own onChange.
      $(card).nodeIds.onAdd(() => emitPlayers());
      $(card).nodeIds.onRemove(() => emitPlayers());
      emitPlayers();
    });
    $(player).fortressCards.onRemove(() => emitPlayers());
    $(player).hiddenCards.onAdd(() => emitPlayers());
    $(player).hiddenCards.onRemove(() => emitPlayers());
    $(player).pendingReorderCards.onAdd(() => emitPlayers());
    $(player).pendingReorderCards.onRemove(() => emitPlayers());
    $(player).pendingMartyrDiscards.onAdd(() => emitPlayers());
    $(player).pendingMartyrDiscards.onRemove(() => emitPlayers());
    $(player).watchedFortresses.onAdd(() => emitPlayers());
    $(player).watchedFortresses.onRemove(() => emitPlayers());
    // Queued moves mutate in place too (slots shift down a turn at a time),
    // so subscribe to each entry's onChange, not just add/remove.
    $(player).queuedMoves.onAdd((m: QueuedMove) => {
      $(m).onChange(emitPlayers);
      emitPlayers();
    });
    $(player).queuedMoves.onRemove(() => emitPlayers());
    // Sparse config overrides live in nested MapSchemas — subscribe so passive
    // Fortress effects that seed/mutate them refresh the roster.
    $(player).buildings.onAdd((b: BuildingConfigState) => {
      $(b).onChange(emitPlayers);
      $(b).range.onChange(emitPlayers);
      emitPlayers();
    });
    $(player).buildings.onRemove(() => emitPlayers());
    $(player).vigorZones.onAdd((z: VigorZoneState) => {
      $(z).onChange(emitPlayers);
      emitPlayers();
    });
    $(player).vigorZones.onRemove(() => emitPlayers());
    emitPlayers();
  });
  $(room.state).players.onRemove((_player: Player, playerId: string) => {
    seatOf.delete(playerId);
    // A departed seat can be some piece's capture origin — repaint those in
    // their current owner's colour rather than a stale seat's.
    scheduleRecolor();
    emitPlayers();
  });

  $(room.state).nodes.onAdd((node: NodeState) => {
    const render = () => watchers.onNode(node, seatOf);
    $(node).onChange(render);
    render();
  });

  const emitPlacements = () => {
    if (!watchers.onPlacements) return;
    const list: PlacementView[] = [];
    room.state.placements.forEach((p: TilePlacement) => {
      list.push({
        slot: p.slot,
        tileId: p.tileId,
        face: p.face,
        rotation: p.rotation,
      });
    });
    watchers.onPlacements(list);
  };
  // Board assembly pushes every tile in one tick; coalesce that burst of onAdd
  // callbacks into a single full-list emit so the client renders the board once.
  let placementsScheduled = false;
  const scheduleEmitPlacements = () => {
    if (placementsScheduled) return;
    placementsScheduled = true;
    queueMicrotask(() => {
      placementsScheduled = false;
      emitPlacements();
    });
  };
  $(room.state).placements.onAdd(() => scheduleEmitPlacements());
  $(room.state).placements.onRemove(() => scheduleEmitPlacements());

  // Severed paths (chasms) — like placements, assembly pushes the whole set in
  // one tick, so coalesce the burst into a single full-list emit.
  const emitSevered = () => {
    if (!watchers.onSevered) return;
    watchers.onSevered([...room.state.severedEdges]);
  };
  let severedScheduled = false;
  const scheduleEmitSevered = () => {
    if (severedScheduled) return;
    severedScheduled = true;
    queueMicrotask(() => {
      severedScheduled = false;
      emitSevered();
    });
  };
  $(room.state).severedEdges.onAdd(() => scheduleEmitSevered());
  $(room.state).severedEdges.onRemove(() => scheduleEmitSevered());

  // Constructed bridges — like severed edges, a build/heal can touch the set in
  // one tick, so coalesce add/remove into a single full-list emit.
  const emitBridges = () => {
    if (!watchers.onBridges) return;
    watchers.onBridges([...room.state.bridges]);
  };
  let bridgesScheduled = false;
  const scheduleEmitBridges = () => {
    if (bridgesScheduled) return;
    bridgesScheduled = true;
    queueMicrotask(() => {
      bridgesScheduled = false;
      emitBridges();
    });
  };
  $(room.state).bridges.onAdd(() => scheduleEmitBridges());
  $(room.state).bridges.onRemove(() => scheduleEmitBridges());

  // Move history — entries are append-only (and trimmed off the front when
  // capped), and an entry's fields/groups never change after it's added, so a
  // coalesced full-list emit on add/remove is enough.
  const emitHistory = () => {
    if (!watchers.onHistory) return;
    const list: HistoryEntryView[] = [];
    room.state.moveHistory.forEach((e: HistoryEntry) => {
      list.push({
        id: e.id,
        playerId: e.playerId,
        playerName: e.playerName,
        seat: e.seat,
        title: e.title,
        round: e.round,
        turn: e.turn,
        focusNodeId: e.focusNodeId,
        groups: e.groups
          ? [...e.groups].map((g: HistoryGroup) => ({
              label: g.label,
              items: g.items ? [...g.items] : [],
            }))
          : [],
      });
    });
    watchers.onHistory(list);
  };
  let historyScheduled = false;
  const scheduleEmitHistory = () => {
    if (historyScheduled) return;
    historyScheduled = true;
    queueMicrotask(() => {
      historyScheduled = false;
      emitHistory();
    });
  };
  $(room.state).moveHistory.onAdd(() => scheduleEmitHistory());
  $(room.state).moveHistory.onRemove(() => scheduleEmitHistory());

  // Restore ring — like the history, entries are immutable once added and only
  // ever appended or dropped, so a coalesced full-list emit is enough.
  const emitRestorePoints = () => {
    if (!watchers.onRestorePoints) return;
    const list: RestorePointView[] = [];
    room.state.restorePoints?.forEach((p: RestorePoint) => {
      list.push({
        id: p.id,
        round: p.round,
        turn: p.turn,
        kind: p.kind,
        playerName: p.playerName,
        seat: p.seat,
        afterHistoryId: p.afterHistoryId,
      });
    });
    watchers.onRestorePoints(list);
  };
  let restoreScheduled = false;
  const scheduleEmitRestorePoints = () => {
    if (restoreScheduled) return;
    restoreScheduled = true;
    queueMicrotask(() => {
      restoreScheduled = false;
      emitRestorePoints();
    });
  };
  // Absent on a server older than the restore ring — the client simply shows
  // no restore affordance.
  if (room.state.restorePoints) {
    $(room.state).restorePoints.onAdd(() => scheduleEmitRestorePoints());
    $(room.state).restorePoints.onRemove(() => scheduleEmitRestorePoints());
  }

  // Chat — like the move history, entries are append-only (trimmed off the
  // front when capped) and immutable once added, so a coalesced full-list emit
  // on add/remove is enough.
  const emitChat = () => {
    if (!watchers.onChat) return;
    const list: ChatMessageView[] = [];
    room.state.chatMessages.forEach((m: ChatMessage) => {
      list.push({
        id: m.id,
        senderId: m.senderId,
        senderName: m.senderName,
        seat: m.seat,
        text: m.text,
        at: m.at,
      });
    });
    watchers.onChat(list);
  };
  let chatScheduled = false;
  const scheduleEmitChat = () => {
    if (chatScheduled) return;
    chatScheduled = true;
    queueMicrotask(() => {
      chatScheduled = false;
      emitChat();
    });
  };
  $(room.state).chatMessages.onAdd(() => scheduleEmitChat());
  $(room.state).chatMessages.onRemove(() => scheduleEmitChat());

  // Top-level scalar state. Register these FIRST: they drive the lobby→playing
  // transition, so they must never be skipped if later wiring throws.
  $(room.state).listen("turnNumber", (value: number) => {
    watchers.onTurn(value);
  });

  $(room.state).listen("roundNumber", (value: number) => {
    watchers.onRound?.(value);
  });

  $(room.state).listen("actionsRemaining", (value: number) => {
    watchers.onActionsRemaining?.(value);
  });

  $(room.state).listen("fortressesImplemented", (value: number) => {
    watchers.onFortressesImplemented?.(value);
  });

  // Tutorial: the step and its targets change together — coalesce.
  const emitTutorial = () => {
    watchers.onTutorial?.({
      active: room.state.tutorial ?? false,
      step: room.state.tutorialStep ?? 0,
      nodeIds: [...(room.state.tutorialNodeIds ?? [])],
      edge: room.state.tutorialEdge ?? "",
    });
  };
  let tutorialScheduled = false;
  const scheduleEmitTutorial = () => {
    if (tutorialScheduled) return;
    tutorialScheduled = true;
    queueMicrotask(() => {
      tutorialScheduled = false;
      emitTutorial();
    });
  };
  if (watchers.onTutorial) {
    $(room.state).listen("tutorial", scheduleEmitTutorial);
    $(room.state).listen("tutorialStep", scheduleEmitTutorial);
    $(room.state).listen("tutorialEdge", scheduleEmitTutorial);
    $(room.state).tutorialNodeIds.onAdd(scheduleEmitTutorial);
    $(room.state).tutorialNodeIds.onRemove(scheduleEmitTutorial);
    emitTutorial();
  }
  $(room.state).listen("startingPlayerCount", (value: number) => {
    watchers.onStartingPlayerCount?.(value);
  });

  $(room.state).listen("undoLabel", (value: string) => {
    watchers.onUndoLabel?.(value ?? "");
  });

  // Space Cheese Curdler's last stand: the fields land together when a win is
  // intercepted, so coalesce them into one emit (like the defense window's
  // flag + list).
  const emitLastStand = () => {
    watchers.onLastStand?.({
      playerId: room.state.graceTurnPlayerId ?? "",
      turnsRemaining: room.state.graceTurnsRemaining ?? 0,
      pendingWinnerId: room.state.pendingWinnerId ?? "",
      pendingWinCardId: room.state.pendingWinCardId ?? "",
    });
  };
  let lastStandScheduled = false;
  const scheduleEmitLastStand = () => {
    if (lastStandScheduled) return;
    lastStandScheduled = true;
    queueMicrotask(() => {
      lastStandScheduled = false;
      emitLastStand();
    });
  };
  $(room.state).listen("graceTurnPlayerId", () => scheduleEmitLastStand());
  $(room.state).listen("graceTurnsRemaining", () => scheduleEmitLastStand());
  $(room.state).listen("pendingWinnerId", () => scheduleEmitLastStand());
  $(room.state).listen("pendingWinCardId", () => scheduleEmitLastStand());

  $(room.state).listen("currentPlayerId", (value: string) => {
    watchers.onCurrentPlayer?.(value);
  });

  $(room.state).listen("firstPlayerId", (value: string) => {
    watchers.onFirstPlayer?.(value);
  });

  $(room.state).listen("roundStartCyclePlayerId", (value: string) => {
    watchers.onRoundStartCycle?.(value);
  });

  $(room.state).listen("phase", (value: string) => {
    watchers.onPhase?.(value);
    emitGameResult();
  });

  // The match result. `winnerId` / `winCardId` land in the same patch that
  // flips `phase` to "ended" (and are already set when joining an ended room),
  // but listener order isn't guaranteed — emit from all three and let the
  // phase gate dedupe the early fires.
  function emitGameResult(): void {
    if (!watchers.onGameResult) return;
    if (room.state.phase !== "ended") return;
    watchers.onGameResult({
      winnerId: room.state.winnerId ?? "",
      winCardId: room.state.winCardId ?? "",
    });
  }
  $(room.state).listen("winnerId", () => emitGameResult());
  $(room.state).listen("winCardId", () => emitGameResult());

  const emitLobbyInfo = () => {
    watchers.onLobbyInfo?.({
      hostUserId: room.state.hostUserId ?? "",
      creatorUserId: room.state.creatorUserId ?? "",
      inviteCode: room.state.inviteCode ?? "",
      visibility: room.state.visibility ?? "public",
      maxPlayers: room.state.maxPlayers ?? 8,
      autoStartAtMs: room.state.autoStartAtMs ?? 0,
      ranked: room.state.ranked ?? false,
      poolId: room.state.poolId ?? "",
      poolName: room.state.poolName ?? "",
    });
  };
  $(room.state).listen("hostUserId", emitLobbyInfo);
  $(room.state).listen("creatorUserId", emitLobbyInfo);
  $(room.state).listen("inviteCode", emitLobbyInfo);
  $(room.state).listen("visibility", emitLobbyInfo);
  $(room.state).listen("maxPlayers", emitLobbyInfo);
  $(room.state).listen("autoStartAtMs", emitLobbyInfo);
  $(room.state).listen("ranked", emitLobbyInfo);
  $(room.state).listen("poolId", emitLobbyInfo);
  $(room.state).listen("poolName", emitLobbyInfo);
  emitLobbyInfo();

  // Defense window: the open/closed flag and the pending-defender list change
  // together when a window opens, so coalesce them into one emit.
  const emitDefense = () => {
    watchers.onDefense?.({
      active: room.state.defenseWindow,
      midTurn: room.state.midTurnDefenseWindow ?? false,
      pending: [...room.state.pendingDefenders],
    });
  };
  let defenseScheduled = false;
  const scheduleEmitDefense = () => {
    if (defenseScheduled) return;
    defenseScheduled = true;
    queueMicrotask(() => {
      defenseScheduled = false;
      emitDefense();
    });
  };
  $(room.state).listen("defenseWindow", () => scheduleEmitDefense());
  $(room.state).listen("midTurnDefenseWindow", () => scheduleEmitDefense());
  $(room.state).pendingDefenders.onAdd(() => scheduleEmitDefense());
  $(room.state).pendingDefenders.onRemove(() => scheduleEmitDefense());

  // Barracks upkeep window — entries land in one burst when combat resolves,
  // so coalesce add/remove into a single full-list emit like the defenders.
  const emitUpkeep = () => {
    watchers.onUpkeep?.([...room.state.pendingUpkeep]);
  };
  let upkeepScheduled = false;
  const scheduleEmitUpkeep = () => {
    if (upkeepScheduled) return;
    upkeepScheduled = true;
    queueMicrotask(() => {
      upkeepScheduled = false;
      emitUpkeep();
    });
  };
  $(room.state).pendingUpkeep.onAdd(() => scheduleEmitUpkeep());
  $(room.state).pendingUpkeep.onRemove(() => scheduleEmitUpkeep());

  // Resource-steal window — same burst-coalescing as the upkeep entries.
  const emitSteals = () => {
    watchers.onSteals?.([...room.state.pendingSteals]);
  };
  let stealsScheduled = false;
  const scheduleEmitSteals = () => {
    if (stealsScheduled) return;
    stealsScheduled = true;
    queueMicrotask(() => {
      stealsScheduled = false;
      emitSteals();
    });
  };
  $(room.state).pendingSteals.onAdd(() => scheduleEmitSteals());
  $(room.state).pendingSteals.onRemove(() => scheduleEmitSteals());

  // Spectator roster — entries are add/remove only (a spectator's name never
  // changes mid-connection), so a full-list emit on either suffices. The map
  // may not be decoded yet when watchState runs (`?.`), so the initial roster
  // arrives via the onAdd burst instead.
  const emitSpectators = () => {
    if (!watchers.onSpectators) return;
    const list: SpectatorView[] = [];
    room.state.spectators?.forEach((s: Spectator, id: string) => {
      list.push({ id, name: s.name });
    });
    watchers.onSpectators(list);
  };
  $(room.state).spectators.onAdd(() => emitSpectators());
  $(room.state).spectators.onRemove(() => emitSpectators());

  // Live cursors — every entry in a patch moves together, so coalesce the
  // per-cursor onChange burst into one full-list emit per patch.
  const emitCursors = () => {
    if (!watchers.onCursors) return;
    const list: CursorView[] = [];
    room.state.cursors?.forEach((c: CursorState, id: string) => {
      list.push({
        id,
        area: c.area,
        x: c.x,
        y: c.y,
        target: c.target,
        tx: c.tx ?? 0.5,
        ty: c.ty ?? 0.5,
      });
    });
    watchers.onCursors(list);
  };
  let cursorsScheduled = false;
  const scheduleEmitCursors = () => {
    if (cursorsScheduled) return;
    cursorsScheduled = true;
    queueMicrotask(() => {
      cursorsScheduled = false;
      emitCursors();
    });
  };
  $(room.state).cursors.onAdd((cursor: CursorState) => {
    $(cursor).onChange(scheduleEmitCursors);
    scheduleEmitCursors();
  });
  $(room.state).cursors.onRemove(() => scheduleEmitCursors());

  // Fortress markets — both decks' face-up rows plus draw/discard pile sizes.
  // Like placements, several cards can change in one tick (deal / refresh /
  // cycle), so coalesce into one emit.
  const pileView = (pile: CardPile | undefined): MarketPile => ({
    cards: [...(pile?.faceUp ?? [])],
    locked: [...(pile?.locked ?? [])],
    deckCount: pile?.hidden?.length ?? 0,
    discardCount: pile?.discarded?.length ?? 0,
    discarded: [...(pile?.discarded ?? [])],
    // Sorted so the hidden pile's draw order never shows through (a tutored
    // card sits on top, known only to the searcher).
    pool: [...(pile?.hidden ?? [])].sort(),
  });
  const emitMarkets = () => {
    if (!watchers.onMarkets) return;
    watchers.onMarkets({
      regular: pileView(room.state.regularCards),
      win: pileView(room.state.winCards),
    });
  };
  let marketsScheduled = false;
  const scheduleEmitMarkets = () => {
    if (marketsScheduled) return;
    marketsScheduled = true;
    queueMicrotask(() => {
      marketsScheduled = false;
      emitMarkets();
    });
  };
  // `regularCards` / `winCards` are nested schemas that may not be decoded when
  // this runs. Reaching `$(room.state).regularCards.faceUp` directly throws in
  // that window (the proxy can't navigate through a not-yet-decoded parent), so
  // instead `listen` for the pile field and attach to its arrays only once we
  // hold a real instance. `immediate` fires now if the pile already exists. We
  // watch every pile (draw / face-up / discard) so the sidebar's deck and
  // discard counts stay live as cards move between them.
  const watchPile = (key: "regularCards" | "winCards") => {
    $(room.state).listen(
      key,
      (pile) => {
        if (!pile) return;
        for (const row of [pile.hidden, pile.faceUp, pile.discarded, pile.locked]) {
          $(row).onAdd(scheduleEmitMarkets);
          $(row).onRemove(scheduleEmitMarkets);
        }
        scheduleEmitMarkets();
      },
      true,
    );
  };
  watchPile("regularCards");
  watchPile("winCards");
}
